/**
 * Executive KPI scorecards for one brand.
 *
 * Each metric carries its value, the change since a stated comparison period,
 * a benchmark, and how far the sample can be trusted. The reliability badge is
 * the important one: a number from 28 respondents and a number from 2,800 look
 * identical otherwise, and only one of them should be acted on.
 */

import React from "react";
import PropTypes from "prop-types";

const RELIABILITY = {
  reliable: { label: "Reliable", hint: "Sufficient sample (n ≥ 100)" },
  directional: { label: "Directional", hint: "Low sample (30–99) — treat as indicative" },
  unreliable: { label: "Low base", hint: "Very low sample (n < 30) — not projectable" },
};

/** Signed change, with the arrow doing the work the colour also does. */
const Delta = ({ delta, period }) => {
  if (delta === undefined || delta === null) return null;
  const direction = delta > 0 ? "up" : delta < 0 ? "down" : "flat";
  const arrow = delta > 0 ? "▲" : delta < 0 ? "▼" : "—";
  const sign = delta > 0 ? "+" : "";
  return (
    <span className={`lou-kpi__delta lou-kpi__delta--${direction}`}>
      {arrow} {sign}
      {delta}
      {period ? <span className="lou-kpi__deltaPeriod"> {period}</span> : null}
    </span>
  );
};

const BrandKpiGrid = ({ brand, period, metrics = [] }) => {
  if (!metrics.length) return null;
  return (
    <section className="lou-kpi" aria-label={`Key metrics for ${brand}`}>
      <header className="lou-kpi__head">
        <h4 className="lou-kpi__brand">{brand}</h4>
        {period ? <span className="lou-kpi__period">{period}</span> : null}
      </header>

      <div className="lou-kpi__grid">
        {metrics.map((m, i) => {
          const reliability = RELIABILITY[m.reliability];
          return (
            <article className="lou-kpi__card" key={`${m.label}-${i}`}>
              <span className="lou-kpi__label">{m.label}</span>
              <span className="lou-kpi__value">
                {m.value}
                {m.unit ? <span className="lou-kpi__unit">{m.unit}</span> : null}
              </span>
              <Delta delta={m.delta} period={m.deltaPeriod} />
              {m.benchmark ? (
                <span className="lou-kpi__benchmark">{m.benchmark}</span>
              ) : null}
              {reliability ? (
                <span
                  className={`lou-kpi__badge lou-kpi__badge--${m.reliability}`}
                  title={reliability.hint}
                >
                  {reliability.label}
                </span>
              ) : null}
            </article>
          );
        })}
      </div>
    </section>
  );
};

Delta.propTypes = { delta: PropTypes.number, period: PropTypes.string };
BrandKpiGrid.propTypes = {
  brand: PropTypes.string,
  period: PropTypes.string,
  metrics: PropTypes.array,
};

export default BrandKpiGrid;
